import React, { useEffect, useState, useMemo } from 'react';
import {
  Zap, Building2, MapPin, Briefcase, ChevronDown, ChevronUp,
  Loader2, TrendingUp, ArrowUpRight, Globe, Activity, Eye,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar, CompanyTypeBadge } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';
import { TierBadge, ConfidenceBadge, TierFilterBar } from '../components/TierBadges';
import { CompanyActionMenu } from '../components/CompanyActionMenu';
import { QuickOutreachModal } from '../components/pipelines/QuickOutreachModal';
import { CompanyEnrichModal } from '../components/CompanyEnrichModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIGNAL_CONFIG = {
  'New Hiring Wave': { color: 'bg-purple-100 text-purple-700 border-purple-200', bar: '#7c3aed', dot: 'bg-purple-500' },
  'Hiring Surge': { color: 'bg-red-100 text-red-700 border-red-200', bar: '#dc2626', dot: 'bg-red-500' },
  'Accelerating': { color: 'bg-orange-100 text-orange-700 border-orange-200', bar: '#ea580c', dot: 'bg-orange-500' },
  'Active Hiring': { color: 'bg-amber-100 text-amber-700 border-amber-200', bar: '#d97706', dot: 'bg-amber-500' },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0A1F44] text-white px-3 py-2 rounded-lg text-xs shadow-xl">
      <p className="font-medium">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || '#fff' }}>{p.name}: {p.value.toLocaleString()}</p>
      ))}
    </div>
  );
};

const HiringSurge = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('signal_strength');
  const [sortDir, setSortDir] = useState('desc');
  const [signalFilter, setSignalFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('');
  const [showStrategic, setShowStrategic] = useState(false);
  const [outreachCompany, setOutreachCompany] = useState(null);
  const [outreachTarget, setOutreachTarget] = useState(null);
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  const fetchData = () => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/hiring-surge?limit=200${filterParams}`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [excludeStaffing, excludeRetail]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const companies = data?.companies || [];
  const tierSummary = data?.tier_summary || {};
  let filtered = signalFilter === 'all' ? companies : companies.filter(c => c.signal === signalFilter);
  if (!showStrategic) filtered = filtered.filter(c => c.priority_tier !== 'strategic_enterprise');
  if (tierFilter) filtered = filtered.filter(c => c.priority_tier === tierFilter);
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'company') return mul * a.company.localeCompare(b.company);
    return mul * ((a[sortField] || 0) - (b[sortField] || 0));
  }), [filtered, sortField, sortDir]);

  const signalDist = data?.signal_distribution || {};
  const surgeCount = (signalDist['Hiring Surge'] || 0) + (signalDist['New Hiring Wave'] || 0);

  // Chart: top 15 surge companies showing this week vs last month
  const chartData = companies.slice(0, 15).map(c => ({
    company: c.company.length > 18 ? c.company.slice(0, 16) + '...' : c.company,
    'This Week': c.jobs_this_week,
    'Last Month': c.jobs_last_month,
  }));

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-3 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap text-xs`}
      onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  return (
    <IntelligenceLayout title="Hiring Surge" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-purple-50 rounded-xl flex items-center justify-center">
            <Zap className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="hiring-surge-title">Hiring Surge Detector</h1>
            <p className="text-sm text-gray-500">Companies that suddenly started hiring aggressively — contact before other agencies</p>
          </div>
        </div>
      </header>

      <main className="p-8 space-y-6">
        <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing}
          excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Detecting hiring surges...
          </div>
        ) : (
          <>
            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="surge-metrics">
              <MetricCard label="Surge Signals" value={surgeCount} icon={Zap} subtext="This week" />
              <MetricCard label="Total Detected" value={data?.total_detected || 0} icon={Building2} />
              <MetricCard label="Best Immediate" value={tierSummary.best_immediate || 0} icon={TrendingUp} subtext="Ready to pursue" />
              <MetricCard label="Needs Enrichment" value={tierSummary.needs_enrichment || 0} icon={Activity} subtext="Verify data" />
              <MetricCard label="Accelerating" value={signalDist['Accelerating'] || 0} icon={Activity} />
            </div>

            <div className="flex items-center gap-4 flex-wrap bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid="tier-filter-section">
              <TierFilterBar activeTier={tierFilter} onTierChange={setTierFilter} tierSummary={tierSummary} showStrategic={showStrategic} />
              <div className="ml-auto">
                <button onClick={() => setShowStrategic(!showStrategic)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all border ${showStrategic ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                  data-testid="toggle-strategic-enterprise">
                  <Eye className="h-3 w-3" /> Strategic Enterprise
                </button>
              </div>
            </div>

            {/* Why This Matters callout */}
            <div className="bg-gradient-to-r from-purple-50 to-red-50 border border-purple-200 rounded-xl p-5" data-testid="surge-context">
              <h3 className="text-sm font-bold text-[#0A1F44] mb-2">Why Sudden Hiring Surges Matter</h3>
              <div className="flex flex-wrap gap-3">
                {(data?.surge_reasons || []).map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/80 border border-purple-200 rounded-full text-xs text-purple-700 font-medium">
                    <ArrowUpRight className="h-3 w-3" /> {r}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">These companies need external recruiting support — contact them before other agencies do.</p>
            </div>

            {/* Signal Filter */}
            <div className="flex items-center gap-3" data-testid="signal-filters">
              <Zap className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600 font-medium">Signal:</span>
              {['all', 'New Hiring Wave', 'Hiring Surge', 'Accelerating', 'Active Hiring'].map(sig => (
                <button key={sig} onClick={() => setSignalFilter(sig)} data-testid={`filter-${sig.toLowerCase().replace(/ /g, '-')}`}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${signalFilter === sig
                    ? 'bg-[#0A1F44] text-white shadow-md'
                    : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                  }`}>
                  {sig === 'all' ? `All (${companies.length})` : `${sig} (${signalDist[sig] || 0})`}
                </button>
              ))}
            </div>

            {/* Comparison Chart */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="surge-chart">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">This Week vs Last Month — Top Surge Companies</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="company" width={130} tick={{ fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Last Month" fill="#94a3b8" radius={[0, 4, 4, 0]} name="Last Month" />
                  <Bar dataKey="This Week" fill="#7c3aed" radius={[0, 4, 4, 0]} name="This Week" />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 bg-[#94a3b8] rounded-sm" /> Previous 23 days</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 bg-[#7c3aed] rounded-sm" /> This week (7 days)</span>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="surge-table">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                <h3 className="text-sm font-semibold text-[#0A1F44]">Companies with Hiring Surge Signals</h3>
                <p className="text-xs text-gray-400 mt-0.5">{sorted.length} companies | This week vs last 30 days</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50/30">
                      <th className="text-left px-3 py-3 w-8">#</th>
                      <SortHeader field="company" label="Company" />
                      <th className="text-center px-3 py-3 text-xs">Tier</th>
                      <SortHeader field="jobs_this_week" label="This Week" align="right" />
                      <SortHeader field="jobs_last_month" label="Last Month" align="right" />
                      <SortHeader field="surge_ratio" label="Surge Ratio" align="right" />
                      <th className="text-center px-3 py-3 text-xs">Signal</th>
                      <th className="text-left px-3 py-3 text-xs">Industry</th>
                      <th className="text-left px-3 py-3 text-xs">Top Role</th>
                      <th className="text-center px-3 py-3 text-xs">Outreach</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((item, i) => {
                      const sig = SIGNAL_CONFIG[item.signal] || SIGNAL_CONFIG['Active Hiring'];
                      return (
                        <tr key={item.company} className="border-t border-gray-100 hover:bg-[#f8f6f0] transition-colors"
                          data-testid={`surge-row-${i}`}>
                          <td className="px-3 py-3 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <WatchlistButton company={item.company} sourceModule="hiring-surge" />
                              <div>
                                <div className="font-semibold text-[#0A1F44]">{item.company}</div>
                                <ConfidenceBadge confidence={item.firmographic_confidence} flags={item.confidence_flags} />
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center"><TierBadge tier={item.priority_tier} /></td>
                          <td className="px-3 py-3 text-right font-bold text-purple-700 text-base">{item.jobs_this_week}</td>
                          <td className="px-3 py-3 text-right text-gray-500">{item.jobs_last_month}</td>
                          <td className="px-3 py-3 text-right">
                            {item.jobs_last_month === 0 ? (
                              <span className="text-purple-600 font-bold text-xs">NEW</span>
                            ) : (
                              <span className={`font-bold text-xs ${item.surge_ratio >= 2 ? 'text-red-600' : item.surge_ratio >= 1 ? 'text-orange-600' : 'text-gray-500'}`}>
                                {item.surge_ratio}x
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${sig.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sig.dot}`} />
                              {item.signal}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            {item.industry ? <div className="flex flex-col gap-1"><span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200 w-fit">{item.industry}</span><CompanyTypeBadge type={item.company_type} /></div> : '—'}
                          </td>
                          <td className="px-3 py-3 text-gray-600 text-xs max-w-[160px] truncate">{item.top_role || '—'}</td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => setOutreachCompany(item.company)} data-testid={`outreach-btn-${i}`}
                                className="px-3 py-1.5 bg-[#0A1F44] text-white rounded-lg text-[10px] font-semibold hover:bg-[#0d2a5c] transition-colors">
                                Outreach
                              </button>
                              <CompanyActionMenu
                                companyName={item.company}
                                companyData={{ industry: item.industry }}
                                onOutreach={(c) => setOutreachTarget(c)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {outreachCompany && <CompanyEnrichModal company={outreachCompany} onClose={() => setOutreachCompany(null)} />}
      {outreachTarget && <QuickOutreachModal company={outreachTarget} onClose={() => setOutreachTarget(null)} onSent={() => setOutreachTarget(null)} />}
    </IntelligenceLayout>
  );
};

export default HiringSurge;
